import {
  AppointmentStatus,
  LegacyAppointmentStatus,
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_OPTIONS,
  isAppointmentStatus,
} from "@/types/enums.types";

/**
 * Translate a status coming from the UI into the value the API expects.
 *
 * Values that are already in API format pass straight through. Legacy
 * kebab-case values (from the pre-migration UI) are mapped. An unrecognised
 * value is reported and returned unchanged rather than silently rewritten —
 * the previous `|| "PENDING"` fallback turned any unmapped status into a
 * wrong-but-valid write, which is far harder to notice than a rejected request.
 */
export function mapFrontendStatusToBackend(
  frontendStatus: AppointmentStatus | LegacyAppointmentStatus | string,
): string {
  if (isAppointmentStatus(frontendStatus)) {
    return frontendStatus;
  }

  const legacyStatusMap: Record<LegacyAppointmentStatus, AppointmentStatus> = {
    "confirmation-pending": "PENDING",
    confirmed: "CONFIRMED",
    // `ON_THE_WAY` is no longer a supported status; the closest live equivalent
    // is CONFIRMED, which is what this mapped to before ON_THE_WAY was added.
    "on-the-way": "CONFIRMED",
    "in-progress": "IN_PROGRESS",
    completed: "COMPLETED",
    cancelled: "CANCELLED",
    "no-answer": "CANCELLED",
    "no-booking": "CANCELLED",
    "not-available": "CANCELLED",
    "not-completed": "NOT_COMPLETED",
    postponed: "POSTPONED",
  };

  const mapped = legacyStatusMap[frontendStatus as LegacyAppointmentStatus];
  if (mapped) {
    return mapped;
  }

  console.error(
    `Unknown appointment status "${frontendStatus}" — sending unchanged.`,
  );
  return frontendStatus;
}

const STATUS_LABELS: Record<AppointmentStatus, string> = Object.fromEntries(
  APPOINTMENT_STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<AppointmentStatus, string>;

/** Human-readable label for a status, tolerant of legacy and unknown values. */
export function getStatusLabel(status?: string): string {
  if (!status) return "Unknown";

  const apiStatus = mapFrontendStatusToBackend(status);
  if (isAppointmentStatus(apiStatus)) {
    return STATUS_LABELS[apiStatus];
  }

  return status;
}

/** Badge classes (light background + text + border) for table/list rows. */
const BADGE_CLASSES: Record<AppointmentStatus, string> = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
  CONFIRMED: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
  IN_PROGRESS:
    "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
  COMPLETED:
    "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200",
  NOT_COMPLETED: "bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-200",
  POSTPONED:
    "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200",
  CANCELLED: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
};

export function getStatusBadgeClasses(status?: string): string {
  const apiStatus = mapFrontendStatusToBackend(status ?? "");
  if (isAppointmentStatus(apiStatus)) {
    return BADGE_CLASSES[apiStatus];
  }
  return "bg-gray-100 text-gray-800 border-gray-200";
}

/** Solid dot/block colours for the calendar view. */
const DOT_CLASSES: Record<AppointmentStatus, string> = {
  PENDING: "bg-orange-500",
  CONFIRMED: "bg-blue-500",
  IN_PROGRESS: "bg-yellow-500",
  COMPLETED: "bg-green-500",
  NOT_COMPLETED: "bg-pink-500",
  POSTPONED: "bg-indigo-500",
  CANCELLED: "bg-red-500",
};

export function getStatusDotClass(status?: string): string {
  const apiStatus = mapFrontendStatusToBackend(status ?? "");
  if (isAppointmentStatus(apiStatus)) {
    return DOT_CLASSES[apiStatus];
  }
  return "bg-gray-500";
}

/** Chart colours, keyed by status, for the reporting dashboards. */
export const STATUS_CHART_COLORS: Record<AppointmentStatus, string> = {
  PENDING: "#f59e0b",
  CONFIRMED: "#3b82f6",
  IN_PROGRESS: "#eab308",
  COMPLETED: "#10b981",
  NOT_COMPLETED: "#f97316",
  POSTPONED: "#6b7280",
  CANCELLED: "#ef4444",
};

export { APPOINTMENT_STATUSES, APPOINTMENT_STATUS_OPTIONS };

/**
 * The business time zone, matching the backend's appointment date lock.
 *
 * The backend decides whether an appointment is "past" using the calendar day
 * in this zone. The browser may be in a different zone, so this must not be
 * derived from `getUserTimezone()` — otherwise a staff member in another zone
 * would see a control enabled that the server then rejects.
 */
const BUSINESS_TIME_ZONE = "Asia/Colombo";

function toBusinessDay(date: Date): string {
  return date.toLocaleDateString("en-CA", { timeZone: BUSINESS_TIME_ZONE });
}

/**
 * True when the appointment's calendar day is before today in the business
 * time zone. Today and future dates are not past.
 */
export function isPastAppointmentDate(date: Date | string): boolean {
  const appointmentDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(appointmentDate.getTime())) {
    return false;
  }
  return toBusinessDay(appointmentDate) < toBusinessDay(new Date());
}

/**
 * Whether the status of an appointment on this date is locked for the current
 * user. Staff may only change today's and future appointments; admins are
 * unrestricted.
 *
 * This mirrors `backend/src/utils/appointmentDateLock.util.ts`. It exists to
 * disable the control rather than to enforce the rule — the server is
 * authoritative and will reject a locked change with a 403.
 */
export function isAppointmentStatusLocked(
  date: Date | string,
  isAdmin: boolean,
): boolean {
  if (isAdmin) {
    return false;
  }
  return isPastAppointmentDate(date);
}
